<template>
  <div class="svoi-checkbox-group" :class="classes">
    <div v-if="label" class="label">{{ label }}</div>
    <div class="items">
      <SvoiCheckbox
        v-for="(item, idx) in preparedItems"
        :key="idx"
        :size="size"
        :disabled="disabled"
        :label="item.label"
        :value="item.checked"
        :model-value="item.checked"
        @input="val => onChange(item, val)"
        @update:model-value="val => onChange(item, val)"
      />
    </div>
    <SvoiHint v-if="error" class="hint" theme="chilli">{{ error }}</SvoiHint>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType, isVue2, ComponentPropsOptions, Ref } from 'vue-demi'
import { SvoiHint } from '@svoi-ui/components/hint'
import { Sizes, SizesEnum, DirectionEnum, Direction } from '@svoi-ui/interfaces/checkbox'
import { isString } from '@svoi-ui/shared/utils/guards'
import SvoiCheckbox from './SvoiCheckbox.vue'

const modelProp: ComponentPropsOptions = isVue2
  ? {
      value: {
        type: Array as PropType<Array<unknown>>,
        default: () => []
      }
    }
  : {
      modelValue: {
        type: Array as PropType<Array<unknown>>,
        default: () => []
      }
    }

export default defineComponent({
  name: 'SvoiCheckboxGroup',
  components: { SvoiCheckbox, SvoiHint },
  props: {
    ...modelProp,
    label: {
      type: String,
      default: ''
    },
    items: {
      type: Array as PropType<Array<Record<string, unknown>>>,
      default: () => []
    },
    itemLabel: {
      type: String,
      default: 'title'
    },
    itemValue: {
      type: String,
      default: 'id'
    },
    size: {
      type: String as PropType<Sizes | string>,
      default: SizesEnum.l,
      validator(value: unknown) {
        return isString(value) && [SizesEnum.l.toString(), SizesEnum.m.toString()].includes(value)
      }
    },
    direction: {
      type: String as PropType<Direction | string>,
      default: DirectionEnum.row,
      validator(value: unknown) {
        return isString(value) && [DirectionEnum.row.toString(), DirectionEnum.column.toString()].includes(value)
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: String as PropType<string | Ref<string>>,
      default: ''
    }
  },
  emits: ['input', 'update:modelValue'],
  setup(props, { emit }) {
    const preparedItems = computed<Array<{ label: string; value: unknown; checked: boolean }>>(() => {
      if (Array.isArray(props.items)) {
        const propValue = isVue2 ? props.value : props.modelValue
        const value = Array.isArray(propValue) ? propValue : []
        return props.items.map((i: Record<string, unknown>) => {
          return {
            label: String(i[props.itemLabel as string]),
            value: i[props.itemValue as string],
            checked: value.includes(i[props.itemValue as string])
          }
        })
      }

      return []
    })

    const classes = computed(() => ({
      [`-direction-${props.direction}`]: true
    }))

    const onChange = (item: { value: unknown; checked: boolean }, val: boolean) => {
      const set = new Set(props.value as Array<unknown>)
      if (val) {
        set.add(item.value)
      } else {
        set.delete(item.value)
      }

      emit(isVue2 ? 'input' : 'update:modelValue', Array.from(set))
    }

    return { preparedItems, classes, onChange }
  }
})
</script>

<style lang="scss">
@use '@svoi-ui/assets/scss/components/checkbox.scss';
</style>
